// Shared Maya block kit — the mcBlock/mcSvg family from the pre-Vue app, in one module so the
// chat cards (MayaCard in MayaView) and the workbench report drawer render blocks identically.
// Adds the block types the first Vue port dropped (secindex / plays / table, needed by
// /api/report-section) and the three interactive ones (contentpick / channels / freqpick) the
// content, outreach and watch cards carry — without them those cards dumped raw JSON.
import { defineComponent, h, onMounted, reactive, ref } from "vue";

import { api } from "../api/client.js";
import { draftBrandOutreach } from "../api/brands.js";
import { useI18n } from "../useI18n.js";
import { mdLite, mayaStream } from "./mayaChat.js";

const { t, lang: i18nLang } = useI18n();

export const MC_CAT = { fix: "var(--verm)", watch: "var(--amber)", win: "var(--jade)", deliverable: "var(--indigo)" };
export const MC_ICON = {
  scan: '<circle cx="12" cy="12" r="7.5"/><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3"/><circle cx="12" cy="12" r="2" fill="currentColor" stroke="none"/>',
  prioritize: '<path d="M6 21V4"/><path d="M6 4.5h11l-2.4 3.2L17 11H6"/>',
  outreach: '<rect x="3" y="5.5" width="18" height="13" rx="2"/><path d="M4 7.5l8 5.5 8-5.5"/>',
  competitors: '<path d="M4 20h16"/><rect x="5" y="11.5" width="3.4" height="6.5" rx="1"/><rect x="10.3" y="6.5" width="3.4" height="11.5" rx="1"/><rect x="15.6" y="14.5" width="3.4" height="3.5" rx="1"/>',
  gaps: '<path d="M10 14a3.2 3.2 0 0 0 4.5 0l3-3a3.2 3.2 0 0 0-4.5-4.5L11.5 8"/><path d="M14 10a3.2 3.2 0 0 0-4.5 0l-3 3a3.2 3.2 0 0 0 4.5 4.5L12.5 16"/>',
  sentiment: '<path d="M20 4H4a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 4 16h3v4l4-4h9a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 20 4z"/><path d="M9 10.5a2.6 2.6 0 0 0 5 0"/>',
  crawler: '<rect x="5" y="8" width="14" height="10" rx="2.5"/><path d="M12 8V5.5"/><circle cx="12" cy="4.3" r="1.1" fill="currentColor" stroke="none"/><circle cx="9.7" cy="12.6" r="1.1" fill="currentColor" stroke="none"/><circle cx="14.3" cy="12.6" r="1.1" fill="currentColor" stroke="none"/><path d="M10 15.4h4"/>',
  audit: '<rect x="5" y="4.5" width="14" height="16" rx="2"/><path d="M9 4.5V3.5h6v1"/><path d="M8.5 12l2.2 2.2L15 10"/>',
  content: '<path d="M5 19l-1 1 1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19z"/><path d="M14.5 6.5l3 3"/>',
  trends: '<path d="M4 16l5-5 3.2 3.2L20 7"/><path d="M15 7h5v5"/>',
  watch: '<path d="M6.5 16.5V11a5.5 5.5 0 0 1 11 0v5.5l1.5 2h-14z"/><path d="M10 19.5a2 2 0 0 0 4 0"/>',
  send: '<path d="M21 3L3.5 10.5 9.5 13 21 3z"/><path d="M9.5 13v6l3.2-3.7"/><path d="M21 3l-3.4 15.5L9.5 13"/>',
  chevron: '<path d="M9 6l6 6-6 6"/>',
  play: '<path d="M8 5v14l11-7z"/>',
};

export function iconNode(name) { return h("svg", { viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false", innerHTML: MC_ICON[name] || MC_ICON.scan }); }

export function valueText(value) { return value == null ? "" : String(value); }

// ── pre-Vue helpers, ported verbatim ──────────────────────────────────────────────────────────

// Client half of the canonical topic_ok rules (link ④): a fragment is never prefilled as a topic.
export function topicOk(q) {
  const s = (q || "").trim();
  const cjk = /[一-鿿぀-ヿ]/.test(s);
  if (s.length < (cjk ? 4 : 8)) return false;
  if (!cjk && s.indexOf(" ") < 0 && s.length < 12) return false;
  if (/^[^A-Za-zÀ-ɏ一-鿿぀-ヿ]+$/.test(s)) return false; // digits/punctuation only
  return true;
}

// Format + site state → the one destination line, so the button caption and the panel that opens
// after the pick can never tell two different stories (batch 3 §2d).
export function destLine(fmt, site) {
  const s = (site && site.state) || "none";
  if (fmt === "blog") return s === "none" ? t("cd_dest_blog_nosite") : t("cd_dest_blog");
  if (fmt === "faq") {
    if (s === "none") return t("cd_dest_faq_nosite");
    if (s === "draft" && (site.draft_faq || (site.faq && site.faq.page))) return t("cd_dest_faq_draft");
    if (site && site.faq && site.faq.page) return String(t("cd_dest_faq")).split("{p}").join(site.faq.page);
    if (site && site.unknown) return t("cd_dest_faq_generic");
    return t("cd_dest_faq_none");
  }
  if (fmt === "llms") return s === "published" ? t("cd_dest_file_live") : s === "draft" ? t("cd_dest_file_draft") : t("cd_dest_file_nosite");
  if (fmt === "reddit" || fmt === "media") return t("cd_dest_offsite");
  return "";
}

// §2a: split a generated FAQ entry into {q,a}. Deliberately conservative — a piece that cannot be
// split cleanly is NOT pushed onto the page; the UI says so and leaves the copy button.
const FAQ_QP = ["q:", "q：", "question:", "问:", "问：", "问题:", "問:", "質問:"];
const FAQ_AP = ["a:", "a：", "answer:", "答:", "答：", "回答:"];
function faqLead(line, list) {
  let s = String(line || "").trim();
  while (s && "#*->".indexOf(s.charAt(0)) >= 0) s = s.slice(1).trim();
  s = s.split("**").join("").trim();
  const lo = s.toLowerCase();
  for (let i = 0; i < list.length; i++) { if (lo.indexOf(list[i]) === 0) return s.slice(list[i].length).trim(); }
  return null;
}
export function faqSplit(text) {
  const lines = String(text || "").split("\n");
  let q = "", a = "", mode = "";
  for (const line of lines) {
    const hq = faqLead(line, FAQ_QP);
    if (hq !== null) { q = hq; mode = "q"; continue; }
    const ha = faqLead(line, FAQ_AP);
    if (ha !== null) { a = ha; mode = "a"; continue; }
    const raw = String(line || "").trim();
    if (!raw) continue;
    if (mode === "q") q = (q ? q + " " : "") + raw;
    else if (mode === "a") a = (a ? a + " " : "") + raw;
  }
  q = q.trim(); a = a.trim();
  return q && a ? { q, a } : null;
}

// outreach draft parsing: SUBJECT/TITLE/MESSAGE/WHY/FOLLOW-UP sections (obParse family)
export function obParse(txt) {
  txt = String(txt || "").replace(/\r/g, "");
  const PATS = [["SUBJECT", /\*\*\s*SUBJECT\s*\*\*/i], ["TITLE", /\*\*\s*TITLE\s*\*\*/i], ["MESSAGE", /\*\*\s*MESSAGE\s*\*\*/i], ["WHY", /\*\*\s*WHY(?:\s+THIS)?(?:\s+WORKS)?\s*\*\*/i], ["FUP", /\*\*\s*FOLLOW[\s\-]?UP\s*\*\*/i]];
  const marks = [];
  PATS.forEach((o) => { const m = o[1].exec(txt); if (m) marks.push({ k: o[0], s: m.index, e: m.index + m[0].length }); });
  if (!marks.length) return { ok: false, raw: txt };
  marks.sort((a, b) => a.s - b.s);
  const secs = {};
  for (let i = 0; i < marks.length; i++) {
    const seg = txt.slice(marks[i].e, i + 1 < marks.length ? marks[i + 1].s : txt.length).replace(/^\s*[—–\-:：]+\s*/, "").trim();
    if (secs[marks[i].k] == null) secs[marks[i].k] = seg;
  }
  const message = secs.MESSAGE || "";
  if (!message) return { ok: false, raw: txt };
  const subject = (secs.SUBJECT || "").split("\n")[0].replace(/^subject\s*[:：]\s*/i, "").trim();
  const title = (secs.TITLE || "").split("\n")[0].trim();
  return { ok: true, subject, title, message, why: secs.WHY || "", followup: secs.FUP || "", raw: txt };
}
// clipboard text: subject line + finished body only (no strategy notes); old format → whole thing
export function obCopyStr(p, raw) { return !p || !p.ok ? raw || "" : (p.subject ? `Subject: ${p.subject}\n\n` : "") + p.message; }
// mailto for email-type drafts only (those with a SUBJECT); body truncated to keep the URL sane
export function obMailtoStr(p) {
  if (!p || !p.ok || !p.subject) return "";
  let body = p.message || "";
  if (body.length > 1800) body = body.slice(0, 1800);
  return `mailto:?subject=${encodeURIComponent(p.subject)}&body=${encodeURIComponent(body)}`;
}

// finished pieces become deliverables silently (autoSaveDeliv); the panel later links to the page
function autoSaveDeliv(kind, title, body, brand) {
  if (!body || body.length < 40) return Promise.resolve(null);
  return api("/api/deliverables", { method: "POST", json: { kind, title, body, brand: brand || "" } })
    .then((d) => (d && d.ok ? d.url || null : null))
    .catch(() => null);
}

// ── interactive blocks (contentpick / channels / freqpick) ────────────────────────────────────
// `bus` is supplied by the host view: articlePlan(brand, q) routes Blog into the article pipeline,
// ask(sentence, faqItem) speaks a deterministic-routing sentence (FAQ merge), history() gives the
// last chat turns for the writer prompt, refreshBrands() reloads the brand cache after a freq
// change. Without a bus (the workbench report drawer) the blocks render, buttons just do nothing.

const lang3 = () => { const l = i18nLang.value; return l === "zh" || l === "ja" ? l : "en"; };
const contentFormatKey = (format) => `cd_format_${format}`;

export const ContentPickBlock = defineComponent({
  name: "ContentPickBlock",
  props: { bl: { type: Object, required: true }, bus: { type: Object, default: null } },
  setup(props) {
    const panel = ref(null);       // {kind:'loading'|'blog'|'files'|'done'|'err', ...}
    const picked = ref("");
    const topicInput = ref("");
    const chipSel = ref(-1);
    const copied = ref(false);
    let copyTimer = 0;
    const liveSite = ref(null);
    const site = () => liveSite.value || (props.bl.site && props.bl.site.state ? props.bl.site : null);

    // A persisted card is a snapshot. The old client reconciled it against the current site cache
    // before showing destinations; fetch the lightweight sites list here so a weeks-old card cannot
    // still tell the owner to upload files manually after a site has been built or published.
    onMounted(async () => {
      try {
        const data = await api("/api/sites");
        const brand = String(props.bl.brand || "").trim().toLowerCase();
        const row = (data?.sites || []).find((item) => String(item.brand || "").trim().toLowerCase() === brand);
        if (!row) return;
        const snap = props.bl.site && props.bl.site.state ? props.bl.site : null;
        if (snap && snap.state === row.status) {
          liveSite.value = { ...snap, live: row.live_url || snap.live || "" };
        } else {
          liveSite.value = { state: row.status || "none", live: row.live_url || "", faq: null, unknown: true, draft_faq: false };
        }
      } catch { /* The embedded snapshot remains the honest fallback while offline. */ }
    });

    function pick(fmt, label) {
      picked.value = fmt;
      // Blog routes to the real article pipeline: the topic card seeds it with the brand's buyer
      // questions so the user can prefill one (or type their own).
      if (fmt === "blog") {
        const list = (props.bl.topics || []).filter(topicOk);
        topicInput.value = list.length ? list[0] : "";
        chipSel.value = list.length ? 0 : -1;
        panel.value = { kind: "blog" };
        return;
      }
      // §2b red line: a live/draft site already serves llms.txt and robots.txt — never offer a
      // manual copy to redo work the site did itself.
      if (fmt === "llms" && ["draft", "published"].includes(site()?.state)) {
        panel.value = { kind: "files" };
        return;
      }
      generate(fmt, label);
    }

    async function generate(fmt, label) {
      const brand = props.bl.brand || "";
      panel.value = { kind: "loading" };
      const fd = t(contentFormatKey(fmt));
      // an FAQ entry is destined for a structured {q,a} slot on the page, so it is REQUESTED in
      // that shape; outbound content stays English regardless of interface language (07-18 rule)
      const shape = fmt === "faq" ? t("cd_faq_generation_shape") : "";
      const prompt = t("cd_generate_prompt", { brand, format: fd, shape });
      const msgs = [...(props.bus?.history?.() || []), { role: "user", content: prompt }];
      const { kind, acc } = await mayaStream(msgs, null, { brand });
      const text = (acc || "").trim();
      if (kind !== "done" || !text) { panel.value = { kind: "err" }; return; }
      panel.value = { kind: "done", fmt, label, text, faq: fmt === "faq" ? faqSplit(text) : null };
      autoSaveDeliv("content", label + (brand ? ` — ${brand}` : ""), text, brand).then((url) => { if (url && panel.value?.kind === "done" && panel.value.fmt === fmt) panel.value = { ...panel.value, url }; });
    }

    function writeArticle() {
      const q = topicInput.value.trim();
      if (!q) return;
      props.bus?.articlePlan?.(props.bl.brand || "", q);
    }
    function copyDone() {
      const done = panel.value;
      if (done?.kind !== "done") return;
      navigator.clipboard?.writeText(done.text).then(() => {
        copied.value = true;
        clearTimeout(copyTimer);
        copyTimer = setTimeout(() => { copied.value = false; }, 1400);
      }).catch(() => { copied.value = false; });
    }

    // mcFaqActions: three honest states — merge it (we know the target block), no FAQ section
    // (offer the page, never invent a block), no site (say so plainly).
    function faqActs(done) {
      const s = site();
      const st = s?.state || "none";
      if (st === "none") return h("div", { class: "mc-cdplace" }, t("cd_faq_nosite_b"));
      if (!(s?.faq?.page) && !(s?.unknown)) return h("div", null, [
        h("div", { class: "mc-cdplace" }, t("cd_faq_none_b")),
        h("div", { class: "mc-cdlinks" }, [h("button", { type: "button", class: "mc-cdlk", onClick: () => props.bus?.ask?.(t("cd_faq_addpage_say")) }, t("cd_faq_addpage"))]),
      ]);
      if (!done.faq) return h("div", { class: "mc-cdplace" }, t("cd_faq_parse"));
      return h("div", { class: "mc-cdlinks" }, [h("button", { type: "button", class: "mc-cdlk", onClick: () => props.bus?.ask?.(t("cd_faq_add_say"), done.faq) }, t("cd_faq_add"))]);
    }

    function panelNode() {
      const p = panel.value;
      if (!p) return null;
      if (p.kind === "loading") return h("div", { class: "obload" }, [h("span", { class: "radar2" }), h("span", t("ob_loading"))]);
      if (p.kind === "err") return h("div", { class: "oberr" }, t("ob_err"));
      if (p.kind === "blog") {
        const list = (props.bl.topics || []).filter(topicOk);
        const chips = list.slice(0, 5).map((q, i) => h("button", { type: "button", class: ["mc-cpk", "mc-topic-chip", chipSel.value === i ? "on" : ""], key: q, onClick: () => { topicInput.value = q; chipSel.value = i; } }, q));
        return h("div", { class: "mc-blogtopic" }, [
          h("div", { class: "mc-obl" }, t("cd_blog_question")),
          h("input", {
            type: "text", class: "art-topic", spellcheck: false,
            placeholder: t(list.length ? "cd_blog_placeholder_examples" : "cd_blog_placeholder_empty"),
            value: topicInput.value,
            onInput: (ev) => { topicInput.value = ev.target.value; chipSel.value = -1; },   // hand-edited text belongs to no chip
            onKeydown: (ev) => { if (ev.key === "Enter") { ev.preventDefault(); writeArticle(); } },
          }),
          chips.length ? h("div", { class: "mc-topicchips" }, chips) : null,
          h("div", { class: "mc-acts" }, [h("button", { type: "button", class: "mc-cta art-topic-go", onClick: writeArticle }, t("cd_write_article"))]),
        ]);
      }
      if (p.kind === "files") {
        const s = site();
        if (s?.state === "published") {
          const live = s.live || "";
          return h("div", { class: "mc-cdnote" }, [
            h("div", { class: "mc-cdh" }, t("cd_have_h")),
            h("div", { class: "mc-cdb" }, t("cd_have_b")),
            live ? h("div", { class: "mc-cdlinks" }, [
              h("a", { class: "mc-cdlk", href: `${live}llms.txt`, target: "_blank", rel: "noopener" }, t("cd_have_llms")),
              h("a", { class: "mc-cdlk", href: `${live}robots.txt`, target: "_blank", rel: "noopener" }, t("cd_have_robots")),
            ]) : null,
          ]);
        }
        return h("div", { class: "mc-cdnote" }, [
          h("div", { class: "mc-cdh" }, t("cd_draft_h")),
          h("div", { class: "mc-cdb" }, t("cd_draft_b")),
          h("div", { class: "mc-cdlinks" }, [h("button", { type: "button", class: "mc-cdlk", onClick: () => props.bus?.ask?.(t("cd_publish")) }, t("cd_publish"))]),
        ]);
      }
      if (p.kind === "done") {
        // §2c: off-site formats say so ABOVE the draft and get NO publish button — we cannot post
        // to Reddit or an outlet, and a button that pretends otherwise is the defect.
        const offNote = p.fmt === "reddit" || p.fmt === "media"
          ? h("div", { class: "mc-cdnote" }, [h("div", { class: "mc-cdh" }, t("cd_off_h")), h("div", { class: "mc-cdb" }, t("cd_off_b"))]) : null;
        // §2b no-site branch: the file IS the deliverable, placement note from the card's place map
        let placeNote = null;
        if (p.fmt === "llms") {
          const place = props.bl.place || {};
          const pl = [place["llms.txt"], place["robots.txt"]].filter(Boolean);
          if (pl.length) placeNote = h("div", { class: "mc-cdplace" }, [h("b", t("cd_place_h")), ` · ${pl.join(" · ")}`]);
        }
        return h("div", null, [
          offNote,
          h("div", { class: "mc-obhead" }, [
            h("span", { class: "mc-obl" }, p.label),
            h("span", null, [
              p.url ? h("a", { class: "mc-obview", href: p.url, target: "_blank", rel: "noopener" }, t("cd_open_page")) : null,
              h("button", { type: "button", class: "mc-obcopy", onClick: copyDone }, copied.value ? t("ob_copied") : t("ob_copy")),
            ]),
          ]),
          h("div", { class: "mc-obbody", innerHTML: mdLite(p.text) }),
          placeNote,
          p.fmt === "faq" ? h("div", { class: "mc-cdacts" }, [faqActs(p)]) : null,
        ]);
      }
      return null;
    }

    return () => [
      h("div", { class: "mc-contentpick" }, (props.bl.items || []).map((x) => h("button", {
        type: "button", class: ["mc-cpk", "mc-cpkf", picked.value === x.k ? "on" : ""], key: x.k,
        onClick: () => pick(x.k, x.label),
      }, [h("span", { class: "mc-cpkl" }, valueText(x.label)), h("span", { class: "mc-cpkd" }, destLine(x.k, site()))]))),
      h("div", { class: "mc-cpanel" }, [panelNode()]),
    ];
  },
});

export const ChannelsBlock = defineComponent({
  name: "ChannelsBlock",
  props: { bl: { type: Object, required: true }, bus: { type: Object, default: null } },
  setup(props) {
    const panels = reactive({});   // domain → {state, raw, parsed}
    const copiedFor = ref("");
    let copyTimer = 0;

    async function draft(domain, regen = false) {
      if (props.bl.bid == null || panels[domain]?.state === "loading") return;
      panels[domain] = { state: "loading" };
      try {
        const d = await draftBrandOutreach(props.bl.bid, { domain, kind: "cited", regen });
        if (d && d.ok && d.content) {
          panels[domain] = { state: "done", raw: d.content, parsed: obParse(d.content) };
          if (!d.cached) {
            autoSaveDeliv("outreach", t("ob_deliverable_title", { domain }), d.content, props.bl.brand || "");
          }
        } else if (d && d.limit) panels[domain] = { state: "limit" };
        else panels[domain] = { state: "err" };
      } catch { panels[domain] = { state: "err" }; }
    }

    function copyFor(domain) {
      const p = panels[domain];
      if (!p || p.state !== "done") return;
      navigator.clipboard?.writeText(obCopyStr(p.parsed, p.raw)).then(() => {
        copiedFor.value = domain;
        clearTimeout(copyTimer);
        copyTimer = setTimeout(() => { copiedFor.value = ""; }, 1600);
      }).catch(() => { copiedFor.value = ""; });
    }

    // main view = subject + ready message; strategy notes fold into a low-key <details>
    function outreachBody(p) {
      if (!p.parsed.ok) return h("div", { class: "ob-msg", innerHTML: mdLite(p.raw) });
      const subj = p.parsed.subject || p.parsed.title;
      const extra = [];
      if (p.parsed.why) extra.push(h("div", { class: "ob-whyp", innerHTML: mdLite(p.parsed.why) }));
      if (p.parsed.followup) extra.push(h("div", { class: "ob-whyp" }, [h("b", `${t("ob_followup")}: `), h("span", { innerHTML: mdLite(p.parsed.followup) })]));
      return [
        subj ? h("div", { class: "ob-subj" }, [h("b", subj)]) : null,
        h("div", { class: "ob-msg", innerHTML: mdLite(p.parsed.message) }),
        extra.length ? h("details", { class: "ob-why" }, [h("summary", t("ob_why")), ...extra]) : null,
      ];
    }

    function rowPanel(domain) {
      const p = panels[domain];
      if (!p) return null;
      if (p.state === "loading") return h("div", { class: "obload" }, [h("span", { class: "radar2" }), h("span", t("ob_loading"))]);
      if (p.state === "limit") return h("div", { class: "oberr" }, t("ob_limit"));
      if (p.state === "err") return h("div", { class: "oberr" }, t("ob_err"));
      const mail = obMailtoStr(p.parsed);
      const brand = props.bl.brand || "";
      return h("div", null, [
        h("div", { class: "mc-obhead" }, [
          h("span", { class: "mc-obtarget" }, [brand ? h("span", [brand, h("span", { class: "mc-obarr" }, "→")]) : null, domain]),
          mail ? h("a", { class: "mc-obmail", href: mail, target: "_blank", rel: "noopener" }, t("ob_open_mail")) : null,
          h("button", { type: "button", class: "mc-obcopy", onClick: () => copyFor(domain) }, copiedFor.value === domain ? t("ob_copied_mail") : t("ob_copy")),
          h("button", { type: "button", class: "mc-obregen", onClick: () => draft(domain, true) }, t("ob_regen")),
        ]),
        h("div", { class: "mc-obbody" }, outreachBody(p)),
      ]);
    }

    return () => h("div", { class: "mc-channels" }, (props.bl.items || []).map((x) => {
      const domain = valueText(x.domain);
      return h("div", { class: "mc-chrow", key: domain }, [
        h("div", { class: "mc-chtop" }, [
          h("span", { class: "mc-chd" }, [domain, x.n ? h("span", { class: "mc-chn" }, t("ob_cites", { n: valueText(x.n) })) : null]),
          h("button", { type: "button", class: "mc-chbtn", onClick: () => draft(domain) }, t("ob_draft")),
        ]),
        h("div", { class: "mc-obpanel" }, [rowPanel(domain)]),
      ]);
    }));
  },
});

export const FreqPickBlock = defineComponent({
  name: "FreqPickBlock",
  props: { bl: { type: Object, required: true }, bus: { type: Object, default: null } },
  setup(props) {
    return () => {
      const cadence = String(props.bl.cur || "weekly").toLowerCase();
      const label = t(`freq_${["manual", "once", "daily", "weekly"].includes(cadence) ? cadence : "weekly"}`);
      return h("p", { class: "mc-freqstatus" }, t("freq_status", { cadence: label }));
    };
  },
});

// mcBlock, ported block for block. `onSection` receives a report section key from the two block
// types that carry one (secindex rows / plays "more") — in the report drawer it switches the
// section in place, exactly like the old data-wbsec delegate. `bus` wires the three interactive
// blocks back to the hosting view (chat cards pass one; the report drawer does not).
export function blockNode(bl, key, onSection, bus) {
  if (!bl || typeof bl !== "object") return h("div", { class: "mc-verdict", style: "font-size:0.90625rem;font-weight:400;color:var(--body);margin-bottom:14px;white-space:pre-wrap", key }, valueText(bl));
  if (bl.type === "contentpick") return h(ContentPickBlock, { bl, bus, key });
  if (bl.type === "channels") return h(ChannelsBlock, { bl, bus, key });
  if (bl.type === "freqpick") return h(FreqPickBlock, { bl, bus, key });
  if (bl.type === "verdict") return h("div", { class: "mc-verdict", key }, [valueText(bl.pre), bl.big != null ? h("span", { class: "mc-big" }, valueText(bl.big)) : null, valueText(bl.post)]);
  if (bl.type === "ladder") return h("div", { key }, [h("div", { class: "mc-ladder" }, (bl.cells || []).map((cell, i) => h("i", { class: valueText(cell), key: i }))), h("div", { class: "mc-ladder-l" }, (bl.labels || []).map((label, i) => h("span", { key: i }, valueText(label))))]);
  if (bl.type === "reads") return h("div", { class: "mc-reads", key }, (bl.items || []).map((item, i) => h("div", { key: i }, [h("div", { class: "mc-n" }, [valueText(item.n), item.sub ? h("small", valueText(item.sub)) : null]), h("div", { class: "mc-l" }, valueText(item.l))])));
  if (bl.type === "rank") return h("div", { class: "mc-rank", key }, (bl.items || []).map((item, i) => h("div", { class: ["mc-rk", item.me || item.you ? "me" : ""], key: i }, [h("span", { class: "mc-rkn" }, String(i + 1)), h("span", { class: "mc-rkname" }, [valueText(item.name || item.label || item.q), item.me || item.you ? h("em", { class: "mc-you" }, valueText(bl.youLabel || t("comp_you"))) : null]), item.pct || item.score || item.v ? h("span", { class: "mc-rp" }, valueText(item.pct || item.score || item.v)) : null])));
  if (bl.type === "grid4") return h("div", { class: "mc-grid4", key }, (bl.items || []).map((item, i) => h("div", { class: "mc-g4c", key: i }, [h("div", { class: "mc-g4l" }, valueText(item.l)), h("div", { class: "mc-g4n" }, [valueText(item.n ?? "—"), item.sub ? h("small", valueText(item.sub)) : null]), item.note ? h("div", { class: "mc-g4s" }, valueText(item.note)) : null])));
  if (bl.type === "riskrow") return h("div", { class: "mc-riskrow", key }, (bl.items || []).map((item, i) => h("span", { class: ["mc-rrk", item.bad ? "bad" : ""], key: i }, [h("i"), h("span", { class: "mc-rrl" }, valueText(item.l)), h("b", valueText(item.v))])));
  if (bl.type === "scoreband") return h("div", { class: ["mc-sb", bl.big ? "big" : ""], key }, [h("div", { class: "mc-sbl" }, [h("div", { class: "mc-sbk" }, valueText(bl.kicker)), h("div", { class: "mc-sbn" }, [valueText(bl.score ?? "—"), h("small", "/100")]), bl.gap ? h("div", { class: "mc-sbg" }, valueText(bl.gap)) : null]), h("div", { class: ["mc-seal", valueText(bl.band)] }, [h("span", { class: "mc-sealb" }, valueText(bl.label)), bl.en && bl.en !== String(bl.label || "").toUpperCase() ? h("span", { class: "mc-sealen" }, valueText(bl.en)) : null])]);
  if (bl.type === "breakdown") {
    const cells = (bl.items || []).map((item, i) => h("div", { class: ["mc-bdc", item.na ? "na" : ""], key: i }, [
      h("div", { class: "mc-bdl" }, valueText(item.l)),
      h("div", { class: "mc-bdv" }, [valueText(item.v), h("small", `/${valueText(item.max)}`)]),
      h("div", { class: "mc-bdbar" }, h("i", { style: { width: `${item.na ? 0 : item.pct || 0}%` } })),
    ]));
    return h("div", { key }, [h("div", { class: "mc-bd" }, cells), bl.note ? h("div", { class: "mc-bdnote" }, valueText(bl.note)) : null]);
  }
  if (bl.type === "sitediff") return h("div", { class: "mc-sitediff", key }, (bl.changes || []).map((ch, i) => h("div", { class: "sd-row", key: i }, [h("div", { class: "sd-h" }, [h("span", { class: "sd-field" }, valueText(ch.field)), ch.page || ch.block ? h("span", { class: "sd-loc" }, [valueText(ch.page), ch.page && ch.block ? " · " : "", valueText(ch.block)]) : null]), ch.before || ch.after ? h("div", { class: "sd-ba" }, [ch.before ? h("span", { class: "sd-before" }, valueText(ch.before)) : null, ch.before && ch.after ? h("span", { class: "sd-arrow" }, "→") : null, ch.after ? h("span", { class: "sd-after" }, valueText(ch.after)) : null]) : null])));
  if (bl.type === "fixchip") return h("div", { class: "mc-fixchip", key }, [iconNode(bl.icon || "crawler"), valueText(bl.text)]);
  if (bl.type === "progress") { const pct = bl.total ? Math.round((Number(bl.done) || 0) / Number(bl.total) * 100) : 0; return h("div", { class: "mc-prog", key }, [h("div", { class: "mc-progr" }, [h("span", { class: "mc-pw" }, valueText(bl.l_week)), h("span", { class: "mc-pd" }, valueText(bl.l_done)), h("span", { class: ["mc-py", bl.yours ? "on" : ""] }, valueText(bl.l_yours))]), h("div", { class: "mc-progbar" }, h("i", { style: { width: `${pct}%` } }))]); }
  if (bl.type === "secindex") return h("div", { key }, [
    bl.title ? h("div", { class: "mc-sihead" }, valueText(bl.title)) : null,
    h("div", { class: "mc-si" }, (bl.items || []).map((item, i) => h("button", {
      type: "button", class: ["mc-sirow", item.dis ? "dis" : ""], key: i, disabled: !!item.dis,
      onClick: () => { if (!item.dis && onSection) onSection(item.k); },
    }, [h("span", { class: "mc-sit" }, valueText(item.label)), h("span", { class: "mc-sid" }, valueText(item.desc)), item.badge ? h("span", { class: "mc-sib" }, valueText(item.badge)) : null, item.dis ? null : h("span", { class: "mc-siar" }, "›")]))),
  ]);
  if (bl.type === "plays") return h("div", { key }, [
    bl.title ? h("div", { class: "mc-sihead" }, valueText(bl.title)) : null,
    h("div", { class: "mc-plays" }, [
      ...(bl.items || []).map((item, i) => h("div", { class: "mc-plr", key: i }, [h("span", { class: "mc-pln" }), h("span", { class: "mc-plt" }, [h("b", valueText(item.a)), item.w ? h("i", valueText(item.w)) : null])])),
      bl.more ? h("button", { type: "button", class: "mc-plmore", onClick: () => { if (onSection) onSection(bl.k || "strategy"); } }, `${valueText(bl.more)} →`) : null,
    ]),
  ]);
  if (bl.type === "table") return h("div", { class: "mdtblwrap", key }, h("table", { class: ["mdtbl", ...(bl.cls ? [String(bl.cls)] : [])] }, [
    h("thead", h("tr", (bl.head || []).map((c, i) => h("th", { key: i }, valueText(c))))),
    h("tbody", (bl.rows || []).map((row, ri) => h("tr", { class: bl.hl != null && ri === bl.hl ? "hl" : "", key: ri }, (row || []).map((c, ci) => h("td", { key: ci }, valueText(c)))))),
  ]));
  if (bl.type === "list") return h("ul", { style: "margin:0 0 14px 18px;font-size:0.84375rem;color:var(--body);line-height:1.55", key }, (bl.items || []).map((item, i) => h("li", { key: i }, valueText(item))));
  if (bl.type === "text") return h("div", { class: "mc-verdict", style: "font-size:0.90625rem;font-weight:400;color:var(--body);margin-bottom:14px;white-space:pre-wrap", key }, valueText(bl.text));
  // unknown block type renders nothing (the pre-Vue mcBlock returned '') — never dump raw JSON
  // into a card; a blank card is honest, a JSON blob is not.
  return null;
}

export function actionNode(action, onAction, key, disabled = false) {
  const cls = action.kind === "cta" ? "mc-cta" : action.kind === "next" ? "mc-next" : "mc-ghost";
  return h("button", {
    type: "button", class: cls, key, disabled,
    "aria-busy": disabled ? "true" : undefined,
    onClick: (event) => onAction(action, event),
  }, [action.kind === "cta" && action.icon ? iconNode(action.icon) : null, valueText(action.label || action.q || action.url || t("mc_act_open"))]);
}
